import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Phone, Mail, FileText, Camera, Check,
  Send, Loader2, ChevronDown, ChevronUp, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { teamApi } from "../../lib/api";
import {
  CHECKLIST_GROUPS, CHECKLIST_STATES, FUEL_LEVELS, DELIVERY_METHODS,
  WINDSCREEN_TYPES, buildEmptyChecklist,
} from "../../lib/checklistTemplate";
import { SignaturePad } from "../../components/team/SignaturePad";
import { PhotoUploader } from "../../components/team/PhotoUploader";
import { DamageDiagram } from "../../components/team/DamageDiagram";
import { StatusPanel, ScheduleChangePanel } from "../../components/team/StatusAndSchedule";

const Section = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white border border-[#cfd7db] rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#edf2f4] hover:bg-[#e2e8eb]"
      >
        <span className="font-display uppercase tracking-wider text-sm text-[#111619]">{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="p-4 space-y-4">{children}</div>}
    </section>
  );
};

const Rating = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`h-10 w-10 rounded-md font-display font-black text-lg transition ${
          n <= value
            ? "bg-[#f28a16] text-white"
            : "bg-[#edf2f4] text-[#9aa6ad]"
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

export const TeamJobDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fitterName, setFitterName] = useState("");
  const [checklist, setChecklist] = useState(buildEmptyChecklist());
  const [photosBefore, setPhotosBefore] = useState([]);
  const [photosAfter, setPhotosAfter] = useState([]);
  const [photosDamage, setPhotosDamage] = useState([]);
  const [clientSig, setClientSig] = useState(null);
  const [fitterSig, setFitterSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showStock, setShowStock] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const j = await teamApi.getJob(id);
        setJob(j);
        setFitterName(j.fitter_team_leader || "");
        if (j.field_submission?.checklist) {
          setChecklist(j.field_submission.checklist);
        }
        if (j.field_submission?.photos) {
          const ps = j.field_submission.photos;
          setPhotosBefore(ps.filter((p) => p.category === "before"));
          setPhotosAfter(ps.filter((p) => p.category === "after"));
          setPhotosDamage(ps.filter((p) => p.category === "damage"));
        }
        if (j.field_submission?.client_signature) setClientSig(j.field_submission.client_signature);
        if (j.field_submission?.fitter_signature) setFitterSig(j.field_submission.fitter_signature);
      } catch (e) {
        toast.error("Could not load job");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const setItem = (itemId, patch) => {
    setChecklist((c) => ({
      ...c,
      items: c.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
    }));
  };

  const submit = async () => {
    if (!clientSig) {
      toast.error("Please capture the client signature.");
      return;
    }
    if (!fitterSig) {
      toast.error("Please capture the fitter signature.");
      return;
    }
    setSubmitting(true);
    try {
      await teamApi.submitJob(id, {
        checklist,
        photos: [...photosBefore, ...photosAfter, ...photosDamage],
        client_signature: clientSig,
        fitter_signature: fitterSig,
        submitted_by: fitterName || job.fitter_team_leader || "field team",
      });
      toast.success("Fitment submitted — admin notified");
      nav("/team");
    } catch (e) {
      console.error(e);
      toast.error("Could not submit. Please check your connection.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center text-[#3f4b52] py-12">Loading…</div>;
  if (!job) return null;
  const isCompleted = job.status === "completed" || job.field_submission?.submitted_at;

  return (
    <div data-testid="team-job-detail">
      <Link to="/team" className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-[#3f4b52]">
        <ArrowLeft size={14} /> Back to today
      </Link>

      <h1 className="mt-2 font-display font-black uppercase text-2xl text-[#111619] leading-tight">
        {job.client_name}
      </h1>
      <div className="text-sm text-[#3f4b52]">
        Job #{job.job_number} · {job.vehicle_make_model || "—"} · <b>{job.registration || "—"}</b>
      </div>
      {isCompleted && (
        <div className="mt-2 inline-flex items-center gap-1.5 bg-[#10b981] text-white text-xs font-display uppercase tracking-widest px-2.5 py-1 rounded-sm">
          <Check size={12} /> Already submitted
        </div>
      )}

      {/* Quick contact */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {job.contact_mobile && (
          <a href={`tel:${job.contact_mobile}`} className="bg-white border border-[#cfd7db] rounded-md p-2.5 flex flex-col items-center gap-1 text-center hover:border-[#f28a16]">
            <Phone size={16} className="text-[#f28a16]" />
            <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Call</span>
          </a>
        )}
        {job.email && (
          <a href={`mailto:${job.email}`} className="bg-white border border-[#cfd7db] rounded-md p-2.5 flex flex-col items-center gap-1 text-center hover:border-[#f28a16]">
            <Mail size={16} className="text-[#f28a16]" />
            <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Email</span>
          </a>
        )}
        {(job.fitment_address || job.fitment_gps) && (
          <a
            href={job.fitment_gps ? `https://maps.google.com/?q=${encodeURIComponent(job.fitment_gps)}` : `https://maps.google.com/?q=${encodeURIComponent(job.fitment_address)}`}
            target="_blank" rel="noopener noreferrer"
            className="bg-white border border-[#cfd7db] rounded-md p-2.5 flex flex-col items-center gap-1 text-center hover:border-[#f28a16]"
          >
            <MapPin size={16} className="text-[#f28a16]" />
            <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Maps</span>
          </a>
        )}
      </div>

      {/* Phase 3: status transitions + schedule change */}
      <div className="mt-4 space-y-3">
        <StatusPanel jobId={job.id} />
        <ScheduleChangePanel jobId={job.id} currentDate={job.fitment_date} currentTime={job.fitment_time} />
        <Link to={`/team/jobs/${job.id}/jobcard`} data-testid="open-jobcard"
              className="block bg-[#111619] text-white rounded-md p-4 flex items-center justify-between hover:bg-[#20282c] transition">
          <div>
            <div className="text-xs font-display uppercase tracking-widest text-[#f28a16]">Phase 4</div>
            <div className="font-display font-bold text-lg">Digital Job Card</div>
            <div className="text-[11px] text-white/70">Pre / Fitment / Post / Sign-off</div>
          </div>
          <span className="text-[#f28a16]">→</span>
        </Link>
      </div>

      <div className="mt-5 space-y-3">
        <Section title="Job briefing" defaultOpen>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-[#3f4b52] uppercase">Date/Time</div><div className="font-semibold">{job.fitment_date || "—"} {job.fitment_time || ""}</div></div>
            <div><div className="text-xs text-[#3f4b52] uppercase">Part code</div><div className="font-semibold">{job.part_code || "—"}</div></div>
            <div><div className="text-xs text-[#3f4b52] uppercase">VIN</div><div className="font-semibold text-xs break-all">{job.vin_number || "—"}</div></div>
            <div><div className="text-xs text-[#3f4b52] uppercase">Engine</div><div className="font-semibold">{job.engine_number || "—"}</div></div>
            {job.payment_type === "insurance" && (
              <>
                <div><div className="text-xs text-[#3f4b52] uppercase">Insurer</div><div className="font-semibold">{job.insurance_company || "—"}</div></div>
                <div><div className="text-xs text-[#3f4b52] uppercase">Claim #</div><div className="font-semibold">{job.claim_number || "—"}</div></div>
              </>
            )}
            <div className="col-span-2">
              <div className="text-xs text-[#3f4b52] uppercase">Location</div>
              <div className="font-semibold">{job.fitment_address || "—"}</div>
            </div>
            {job.notes && (
              <div className="col-span-2 bg-[#fff7ed] border border-[#fb923c] rounded-md p-2 text-xs text-[#7c2d12]">
                <b className="uppercase tracking-widest text-xs">Notes:</b> {job.notes}
              </div>
            )}
          </div>
        </Section>

        <Section title="Vehicle check-in — before fitment" defaultOpen={false}>
          {CHECKLIST_GROUPS.map((g) => (
            <div key={g.id}>
              <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-2">{g.label}</div>
              <ul className="space-y-2">
                {checklist.items
                  .filter((it) => g.items.some((x) => x.id === it.id))
                  .map((it) => (
                    <li key={it.id} className="bg-[#f5f6f6] rounded-md p-2.5">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-semibold text-sm text-[#111619]">{it.label}</div>
                        <div className="flex gap-1 flex-wrap">
                          {CHECKLIST_STATES.map((s) => (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => setItem(it.id, { state: s.value })}
                              className={`px-2.5 py-1 rounded-sm font-display uppercase tracking-wider text-xs ${
                                it.state === s.value
                                  ? "text-white"
                                  : "bg-white text-[#3f4b52] border border-[#cfd7db]"
                              }`}
                              style={it.state === s.value ? { background: s.color } : undefined}
                              data-testid={`chk-${it.id}-${s.value}`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {(it.state === "damaged" || it.state === "missing") && (
                        <Input
                          value={it.note || ""}
                          onChange={(e) => setItem(it.id, { note: e.target.value })}
                          placeholder="Describe the issue"
                          className="mt-2 bg-white border-[#cfd7db] h-9"
                        />
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <Label className="text-xs uppercase text-[#3f4b52]">Fuel level</Label>
              <div className="flex gap-1.5 mt-1.5">
                {FUEL_LEVELS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setChecklist((c) => ({ ...c, fuel_level: f }))}
                    className={`flex-1 h-10 rounded-md font-display font-bold ${checklist.fuel_level === f ? "bg-[#f28a16] text-white" : "bg-[#edf2f4] text-[#3f4b52]"}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#3f4b52]">Windscreen type</Label>
              <select
                value={checklist.windscreen_type}
                onChange={(e) => setChecklist((c) => ({ ...c, windscreen_type: e.target.value }))}
                className="mt-1.5 w-full h-10 bg-white border border-[#cfd7db] rounded-md px-3 text-sm"
              >
                <option value="">Select…</option>
                {WINDSCREEN_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase text-[#3f4b52]">Delivery method</Label>
              <select
                value={checklist.delivery_method}
                onChange={(e) => setChecklist((c) => ({ ...c, delivery_method: e.target.value }))}
                className="mt-1.5 w-full h-10 bg-white border border-[#cfd7db] rounded-md px-3 text-sm"
              >
                <option value="">Select…</option>
                {DELIVERY_METHODS.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
        </Section>

        <Section title="Damage diagram & before photos" defaultOpen={false}>
          <DamageDiagram
            damages={checklist.damages}
            onChange={(d) => setChecklist((c) => ({ ...c, damages: d }))}
            testid="damage-diagram"
          />
          <PhotoUploader
            label="Photos of existing damage"
            category="damage"
            photos={photosDamage}
            onChange={setPhotosDamage}
            testid="photos-damage"
          />
          <PhotoUploader
            label="Photos BEFORE fitment"
            category="before"
            photos={photosBefore}
            onChange={setPhotosBefore}
            testid="photos-before"
          />
        </Section>

        <Section title="After fitment" defaultOpen={false}>
          <PhotoUploader
            label="Photos AFTER fitment"
            category="after"
            photos={photosAfter}
            onChange={setPhotosAfter}
            testid="photos-after"
          />

          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Customer service rating</Label>
            <Rating
              value={checklist.rating_customer_service}
              onChange={(v) => setChecklist((c) => ({ ...c, rating_customer_service: v }))}
              testid="rating-cs"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Premises / on-site setup rating</Label>
            <Rating
              value={checklist.rating_premises}
              onChange={(v) => setChecklist((c) => ({ ...c, rating_premises: v }))}
              testid="rating-prem"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Quality of work rating</Label>
            <Rating
              value={checklist.rating_quality_of_work}
              onChange={(v) => setChecklist((c) => ({ ...c, rating_quality_of_work: v }))}
              testid="rating-qow"
            />
          </div>

          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Notes / additional comments</Label>
            <Textarea
              value={checklist.notes}
              onChange={(e) => setChecklist((c) => ({ ...c, notes: e.target.value }))}
              rows={3}
              className="mt-1.5 bg-white border-[#cfd7db]"
              data-testid="checklist-notes"
            />
          </div>
        </Section>

        <Section title="Signatures" defaultOpen={false}>
          <SignaturePad
            label="Client signature"
            value={clientSig}
            onChange={setClientSig}
            testid="sig-client"
          />
          <SignaturePad
            label="Fitter signature"
            value={fitterSig}
            onChange={setFitterSig}
            testid="sig-fitter"
          />
          <div>
            <Label className="text-xs uppercase text-[#3f4b52]">Submitting fitter (name)</Label>
            <Input
              value={fitterName}
              onChange={(e) => setFitterName(e.target.value)}
              placeholder="Your name"
              className="mt-1.5 bg-white border-[#cfd7db] h-10"
              data-testid="fitter-name"
            />
          </div>
        </Section>

        {/* Quick stock request */}
        <button
          type="button"
          onClick={() => setShowStock((s) => !s)}
          className="w-full bg-[#20282c] text-white rounded-md py-3 font-display uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
          data-testid="quick-stock-btn"
        >
          <Boxes size={14} /> Request stock for this job
        </button>
        {showStock && <QuickStock jobId={id} fitter={fitterName} onDone={() => setShowStock(false)} />}

        {/* Submit */}
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid="team-submit-btn"
          className="w-full bg-[#f28a16] text-white rounded-md py-4 font-display uppercase tracking-widest text-sm shadow-lg inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {submitting ? "Submitting…" : "Submit completed fitment"}
        </button>
      </div>
    </div>
  );
};

// Quick inline stock request inside the job
const QuickStock = ({ jobId, fitter, onDone }) => {
  const [form, setForm] = useState({ part_item: "", quantity: 1, urgency: "normal", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.part_item.trim()) return;
    setBusy(true);
    try {
      await teamApi.createStockRequest({ ...form, job_id: jobId, requested_by: fitter });
      toast.success("Stock request sent to admin");
      onDone();
    } catch {
      toast.error("Could not send request");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-[#cfd7db] rounded-md p-4 space-y-3">
      <Input value={form.part_item} onChange={(e) => setForm({ ...form, part_item: e.target.value })} placeholder="Part / item required" className="bg-white border-[#cfd7db]" data-testid="quick-stock-item" />
      <div className="grid grid-cols-2 gap-2">
        <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} className="bg-white border-[#cfd7db]" />
        <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className="h-10 bg-white border border-[#cfd7db] rounded-md px-2 text-sm">
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>
      <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes (optional)" className="bg-white border-[#cfd7db]" />
      <button type="submit" disabled={busy} className="w-full bg-[#f28a16] text-white rounded-md py-2.5 font-display uppercase tracking-widest text-xs" data-testid="quick-stock-submit">
        {busy ? "Sending…" : "Send to admin"}
      </button>
    </form>
  );
};

export default TeamJobDetail;
